import {
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SxProps,
} from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsReportEmployeeScope } from '~/data/reports';
import { AnalyticsReportConfig } from '~/scenes/PortalModules/Analytics/common/ReportDatasetSelector/data';
import { useReportEmployeeScopeSelector } from '~/scenes/PortalModules/Analytics/common/ReportDatasetSelector/ReportEmployeeScopeSelector/useReportEmployeeScopeSelector';
import { useReportEmployeeScopeSelectorOptions } from '~/scenes/PortalModules/Analytics/common/ReportDatasetSelector/ReportEmployeeScopeSelector/useReportEmployeeScopeSelectorOptions';

type ReportEmployeeScopeSelectorProps = {
  employeeScope?: AnalyticsReportEmployeeScope;
  onChange: (empScope: AnalyticsReportEmployeeScope) => void;
  reportConfig: AnalyticsReportConfig;
  sx?: SxProps;
};

export const ReportEmployeeScopeSelector = (
  props: ReportEmployeeScopeSelectorProps,
) => {
  const { reportConfig, employeeScope, onChange, sx } = props;

  const { t } = useTranslation();

  const { selectOptions } = useReportEmployeeScopeSelector({
    reportConfig,
    employeeScope,
  });

  const select = useMemo(() => {
    const selectId = 'optionalDataSelector';

    return {
      id: selectId,
      label: t('employeeScope'),
      labelId: `${selectId}-label`,
      inputId: `${selectId}-input`,
    };
  }, [t]);

  return (
    <FormControl size={'small'} fullWidth={true} sx={sx}>
      <InputLabel id={select.labelId} size={'small'}>
        {select.label}
      </InputLabel>
      <Select
        labelId={select.labelId}
        id={select.id}
        value={employeeScope}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        input={<OutlinedInput id={select.inputId} label={select.label} />}
        size={'small'}
        fullWidth={true}
      >
        {selectOptions.map(({ value, label, description }, index) => (
          <MenuItem
            key={`option-${index}`}
            value={value}
            selected={selectOptions.some((option) => option.value === value)}
          >
            <ListItemText secondary={description} sx={{ margin: 0 }}>
              {label}
            </ListItemText>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

type ReportEmployeeScopeValueProps = {
  empScope?: AnalyticsReportEmployeeScope;
};

export const ReportEmployeeScopeValue = (
  props: ReportEmployeeScopeValueProps,
) => {
  const { empScope } = props;

  const { getEmployeeScopeOption } = useReportEmployeeScopeSelectorOptions();

  return <>{getEmployeeScopeOption(empScope)?.label}</>;
};
